import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Plus, X, Trash2 } from "lucide-react";
import {
  usePurchaseQuotes,
  usePurchaseQuote,
  useCreatePurchaseQuote,
  useVoidPurchaseQuote,
  useConvertPurchaseQuote,
  PURCHASE_QUOTE_STATUS_LABEL,
  PurchaseQuoteStatus,
} from "@/api/purchase-quotes";
import { useSuppliers, useProducts, useBranches } from "@/api/inventory";
import { getApiErrorMessage } from "@/api/client";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

const STATUS_STYLE: Record<PurchaseQuoteStatus, string> = {
  ABIERTA: "bg-blue-100 text-blue-700",
  CONVERTIDA: "bg-green-100 text-green-700",
  ANULADA: "bg-red-100 text-red-700",
};

/** Cotizaciones de compra: precio estimado que da un proveedor ANTES de convertirse en una orden de compra real — no toca inventario ni cuentas por pagar. */
export default function PurchaseQuotesPage() {
  const [status, setStatus] = useState<PurchaseQuoteStatus | "">("ABIERTA");
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading } = usePurchaseQuotes({ status: status || undefined, pageSize: 50 });
  const quotes = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Cotizaciones de compra</h1>
          <p className="text-sm text-gray-500">Registra el precio que ofrece un proveedor antes de generar la orden de compra; conviértela cuando confirmes.</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={16} className="mr-1.5" /> Nueva cotización
        </button>
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <select className="input-field w-auto" value={status} onChange={(e) => setStatus(e.target.value as PurchaseQuoteStatus | "")}>
          <option value="">Todos los estados</option>
          {Object.entries(PURCHASE_QUOTE_STATUS_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">#</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Fecha</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Proveedor</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Vigencia</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Total estimado</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && quotes.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  No hay cotizaciones de compra con este filtro.
                </td>
              </tr>
            )}
            {quotes.map((q) => (
              <tr key={q.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setSelectedId(q.id)}>
                <td className="px-4 py-3 text-gray-600">#{q.quoteNumber}</td>
                <td className="px-4 py-3 text-gray-600">{q.quoteDate}</td>
                <td className="px-4 py-3 text-gray-900">{q.supplier?.name ?? "—"}</td>
                <td className="px-4 py-3 text-gray-600">
                  {q.validUntil ?? "—"} {q.isExpired && <span className="ml-1 text-xs font-medium text-red-600">(vencida)</span>}
                </td>
                <td className="px-4 py-3 text-right text-gray-900">{currency.format(Number(q.total))}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[q.status]}`}>{PURCHASE_QUOTE_STATUS_LABEL[q.status]}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => setSelectedId(q.id)}>
                    Ver
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreatePurchaseQuoteModal onClose={() => setShowCreate(false)} />}
      {selectedId && <PurchaseQuoteDetailModal id={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

function PurchaseQuoteDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: quote, isLoading } = usePurchaseQuote(id);
  const voidQuote = useVoidPurchaseQuote();
  const convertQuote = useConvertPurchaseQuote();

  const [purchaseDate, setPurchaseDate] = useState(todayStr());
  const [invoiceNumber, setInvoiceNumber] = useState("");

  const handleVoid = async () => {
    if (!window.confirm("¿Anular esta cotización de compra?")) return;
    try {
      await voidQuote.mutateAsync(id);
      toast.success("Cotización de compra anulada.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleConvert = async () => {
    try {
      await convertQuote.mutateAsync({ id, purchaseDate, invoiceNumber: invoiceNumber || undefined });
      toast.success("Cotización convertida en orden de compra.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Cotización de compra {quote ? `#${quote.quoteNumber}` : ""}</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {isLoading && <p className="text-sm text-gray-400">Cargando...</p>}
          {quote && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Proveedor</p>
                  <p className="font-medium text-gray-900">{quote.supplier?.name ?? "—"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Sucursal</p>
                  <p className="font-medium text-gray-900">{quote.branch?.name ?? "—"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Vigencia</p>
                  <p className="font-medium text-gray-900">
                    {quote.validUntil ?? "Sin vencimiento"} {quote.isExpired && <span className="text-red-600">(vencida)</span>}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Total estimado</p>
                  <p className="font-semibold text-gray-900">{currency.format(Number(quote.total))}</p>
                </div>
              </div>

              <div>
                <p className="mb-1 text-sm font-medium text-gray-700">Productos</p>
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-500">Producto</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-500">Cant.</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-500">Costo estimado</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-500">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {quote.lines.map((l) => (
                      <tr key={l.id}>
                        <td className="px-2 py-1.5">{l.product?.name ?? l.productId}</td>
                        <td className="px-2 py-1.5 text-right">{l.quantity}</td>
                        <td className="px-2 py-1.5 text-right">{currency.format(Number(l.estimatedUnitCost))}</td>
                        <td className="px-2 py-1.5 text-right">{currency.format(Number(l.subtotal))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {quote.notes && (
                <div>
                  <p className="text-xs font-medium text-gray-500">Notas</p>
                  <p className="text-sm text-gray-700">{quote.notes}</p>
                </div>
              )}

              {quote.status === "CONVERTIDA" && (
                <p className="rounded-md bg-green-50 p-3 text-sm text-green-700">
                  Convertida en una orden de compra (BORRADOR) — recíbela desde Compras cuando llegue la mercancía.
                </p>
              )}

              {quote.status === "ABIERTA" && (
                <div className="space-y-2 rounded-md bg-gray-50 p-3">
                  <p className="text-sm font-medium text-gray-700">Convertir en orden de compra</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500">Fecha de la orden</label>
                      <input className="input-field mt-1" type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">N.° de factura del proveedor (opcional)</label>
                      <input className="input-field mt-1" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" className="btn-secondary flex-1" disabled={voidQuote.isPending} onClick={handleVoid}>
                      Anular cotización
                    </button>
                    <button type="button" className="btn-primary flex-1" disabled={!purchaseDate || convertQuote.isPending} onClick={handleConvert}>
                      {convertQuote.isPending ? "Convirtiendo..." : "Convertir en orden de compra"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface DraftLine {
  productId: string;
  quantity: string;
  estimatedUnitCost: string;
}

function CreatePurchaseQuoteModal({ onClose }: { onClose: () => void }) {
  const { data: branches } = useBranches(true);
  const { data: suppliers } = useSuppliers(true);
  const { data: productsResponse } = useProducts({ pageSize: 200, isActive: true });
  const products = productsResponse?.data ?? [];
  const createQuote = useCreatePurchaseQuote();

  const [branchId, setBranchId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([{ productId: "", quantity: "1", estimatedUnitCost: "0" }]);

  useEffect(() => {
    if (!branchId && branches && branches.length > 0) {
      setBranchId(branches.find((b) => b.isMain)?.id ?? branches[0].id);
    }
  }, [branches, branchId]);

  const addLine = () => setLines((prev) => [...prev, { productId: "", quantity: "1", estimatedUnitCost: "0" }]);
  const removeLine = (index: number) => setLines((prev) => prev.filter((_, i) => i !== index));
  const updateLine = (index: number, patch: Partial<DraftLine>) => setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const isValid = branchId && supplierId && lines.every((l) => l.productId && Number(l.quantity) > 0 && Number(l.estimatedUnitCost) >= 0);

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error("Elige sucursal, proveedor y completa cada línea con producto, cantidad y costo estimado.");
      return;
    }
    try {
      await createQuote.mutateAsync({
        branchId,
        supplierId,
        validUntil: validUntil || undefined,
        notes: notes || undefined,
        lines: lines.map((l) => ({ productId: l.productId, quantity: l.quantity, estimatedUnitCost: l.estimatedUnitCost })),
      });
      toast.success("Cotización de compra creada.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Nueva cotización de compra</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Sucursal</label>
              <select className="input-field mt-1" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">Seleccione...</option>
                {branches?.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Proveedor</label>
              <select className="input-field mt-1" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                <option value="">Seleccione...</option>
                {suppliers?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Vigente hasta (opcional)</label>
            <input className="input-field mt-1 w-48" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <p className="text-sm font-medium text-gray-700">Productos</p>
              <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={addLine}>
                + Agregar línea
              </button>
            </div>
            <div className="space-y-2">
              {lines.map((line, index) => (
                <div key={index} className="grid grid-cols-12 items-center gap-2">
                  <select className="input-field col-span-6" value={line.productId} onChange={(e) => updateLine(index, { productId: e.target.value })}>
                    <option value="">Producto...</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.sku} — {p.name}
                      </option>
                    ))}
                  </select>
                  <input
                    className="input-field col-span-2"
                    type="number"
                    placeholder="Cantidad"
                    value={line.quantity}
                    onChange={(e) => updateLine(index, { quantity: e.target.value })}
                  />
                  <input
                    className="input-field col-span-3"
                    type="number"
                    placeholder="Costo estimado"
                    value={line.estimatedUnitCost}
                    onChange={(e) => updateLine(index, { estimatedUnitCost: e.target.value })}
                  />
                  <button type="button" className="col-span-1 text-gray-400 hover:text-red-600" onClick={() => removeLine(index)} aria-label="Quitar línea">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Notas (opcional)</label>
            <textarea className="input-field mt-1" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={!isValid || createQuote.isPending} onClick={handleSubmit}>
            {createQuote.isPending ? "Guardando..." : "Crear cotización"}
          </button>
        </div>
      </div>
    </div>
  );
}
